import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useUIStore, useMovieStore, useEditorStore } from '../../../stores';
import { usePatch } from '../../../hooks';
import { useTranslation } from '../../../i18n';
import { Toolpad } from '../Toolpad';
import { PropertyInput } from '../../ui/PropertyInput';
import { useRef, useEffect, useState } from 'react';
import { Select } from '@radix-ui/themes';
import { publishMovie, resolvePlayerUrl } from '../../../util/publishMovie';
export function MoviePropertiesToolpad() {
    const { t } = useTranslation();
    const { toolpads, toggleToolpad } = useUIStore();
    const { currentMovie, updateMovie } = useMovieStore();
    const { setFps } = useEditorStore();
    const isOpen = toolpads.movieProperties.isOpen;
    const movieId = currentMovie?.id ? Number(currentMovie.id) : null;
    const [publishing, setPublishing] = useState(false);
    const [publishError, setPublishError] = useState(null);
    const [publishLink, setPublishLink] = useState(null);
    const [copied, setCopied] = useState(false);
    const updateMovieUrl = movieId && !Number.isNaN(movieId) ? `/api/movies/${movieId}/update` : null;
    const { execute: saveMovieToApi, error: saveMovieApiError } = usePatch(updateMovieUrl);
    const hasPendingSaveRef = useRef(false);
    const saveTimeoutRef = useRef(null);
    useEffect(() => {
        if (!currentMovie || !movieId || Number.isNaN(movieId))
            return;
        if (!hasPendingSaveRef.current)
            return;
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            hasPendingSaveRef.current = false;
            void saveMovieToApi({
                name: currentMovie.name,
                description: currentMovie.description,
                width: currentMovie.width,
                height: currentMovie.height,
                fps: currentMovie.fps,
            });
        }, 600);
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [
        currentMovie,
        movieId,
        saveMovieToApi,
        currentMovie?.name,
        currentMovie?.description,
        currentMovie?.width,
        currentMovie?.height,
        currentMovie?.fps,
    ]);
    const updateMovieWithDelaySave = (updates) => {
        hasPendingSaveRef.current = true;
        updateMovie(updates);
        if (typeof updates.fps === 'number') {
            setFps(updates.fps);
        }
    };
    const handleFpsChange = (fps) => {
        updateMovieWithDelaySave({ fps });
    };
    const handlePublish = async () => {
        if (!movieId || Number.isNaN(movieId) || publishing)
            return;
        setPublishing(true);
        setPublishError(null);
        setCopied(false);
        try {
            const result = await publishMovie(movieId);
            const url = resolvePlayerUrl(result);
            setPublishLink(url);
            try {
                await navigator.clipboard.writeText(url);
                setCopied(true);
            }
            catch {
                // clipboard may be blocked; link still shown
            }
        }
        catch (e) {
            setPublishError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setPublishing(false);
        }
    };
    return (_jsx(Toolpad, { title: t('toolpads.movieProperties.title'), isOpen: isOpen, onToggle: () => toggleToolpad('movieProperties'), children: currentMovie ? (_jsxs("div", { className: "movie-properties", children: [saveMovieApiError && _jsx("div", { className: "error-message", children: saveMovieApiError }), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('toolpads.movieProperties.stageSize') }), _jsxs("div", { className: "property-grid", children: [_jsx(PropertyInput, { label: t('toolpads.movieProperties.width'), value: currentMovie.width, onChange: (v) => updateMovieWithDelaySave({ width: v }), min: 100, max: 4096 }), _jsx(PropertyInput, { label: t('toolpads.movieProperties.height'), value: currentMovie.height, onChange: (v) => updateMovieWithDelaySave({ height: v }), min: 100, max: 4096 })] }), _jsxs("div", { className: "preset-sizes", children: [_jsx("button", { className: "btn btn-tiny", onClick: () => updateMovieWithDelaySave({ width: 800, height: 600 }), children: "800\u00D7600" }), _jsx("button", { className: "btn btn-tiny", onClick: () => updateMovieWithDelaySave({ width: 1280, height: 720 }), children: "720p" }), _jsx("button", { className: "btn btn-tiny", onClick: () => updateMovieWithDelaySave({ width: 1920, height: 1080 }), children: "1080p" })] })] }), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('toolpads.movieProperties.frameRate') }), _jsx("div", { className: "property-row-full", children: _jsxs(Select.Root, { value: String(currentMovie.fps), onValueChange: (value) => handleFpsChange(Number.parseInt(value, 10)), size: "1", children: [_jsx(Select.Trigger, { variant: "surface", className: "style-select-trigger", "aria-label": t('toolpads.movieProperties.frameRateAria') }), _jsx(Select.Content, { position: "popper", sideOffset: 4, children: [12, 24, 30, 60].map((fps) => (_jsx(Select.Item, { value: String(fps), children: t('toolpads.movieProperties.fpsOption', { count: fps }) }, fps))) })] }) })] }), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('toolpads.movieProperties.info') }), _jsxs("div", { className: "movie-info", children: [_jsxs("div", { children: [t('toolpads.movieProperties.name'), _jsx("strong", { children: currentMovie.name })] }), _jsxs("div", { children: [t('toolpads.movieProperties.scenes'), _jsx("strong", { children: currentMovie.scenes.length })] })] })] }), _jsxs("div", { className: "property-section", children: [_jsx("div", { className: "property-section-title", children: t('toolpads.movieProperties.publish') }), _jsx("p", { className: "movie-properties__publish-hint", children: t('toolpads.movieProperties.publishHint') }), _jsx("button", { type: "button", className: "btn btn-tiny", onClick: () => void handlePublish(), disabled: publishing || !movieId, children: publishing
                                ? t('toolpads.movieProperties.publishing')
                                : t('toolpads.movieProperties.publish') }), publishError && _jsx("div", { className: "error-message", children: publishError }), publishLink && (_jsxs("div", { className: "movie-properties__publish-result", children: [_jsx("a", { href: publishLink, target: "_blank", rel: "noreferrer", children: publishLink }), copied && (_jsx("span", { className: "movie-properties__copied", children: t('toolpads.movieProperties.linkCopied') }))] }))] })] })) : (_jsx("div", { className: "no-selection-message", children: t('toolpads.movieProperties.noMovie') })) }));
}
